/**
 * T-109: the QR "regenerate" ceremony dialog.
 *
 * Regenerating retires the unit's current PERMANENT sticker and mints a new
 * one, so it is superadmin-only and deliberate: the confirm button stays
 * disabled until the exact phrase `Regenerate <unit_code> QR Code` is typed.
 * The action is audited server-side as QR_CODE_REGENERATED.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { regenerateUnitQr, regeneratePhraseFor } from '@/lib/qrCodeApi';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  unitCode: string;
  onDone: () => void;
}

export function RegenerateQrDialog({ open, onOpenChange, unitId, unitCode, onDone }: Props) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const expected = regeneratePhraseFor(unitCode);
  const armed = typed.trim() === expected;

  // Reset in the close handler (avoids a set-state-in-effect cascade); the parent
  // also unmounts the dialog on close, so this is belt-and-suspenders.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setTyped('');
      setSubmitting(false);
    }
    onOpenChange(next);
  }

  async function handleSubmit() {
    if (!armed) return;
    setSubmitting(true);
    try {
      await regenerateUnitQr(unitId, typed.trim());
      toast.success('New QR generated and activated.');
      onDone();
      handleOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Regenerate failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Regenerate QR code</DialogTitle>
          <DialogDescription className="space-y-2">
            <span>
              This retires the unit’s current QR sticker and mints a new one. The
              old code stays in history and still resolves when scanned, but it
              will no longer be the active code.
            </span>
            <span className="block">
              To confirm, type{' '}
              <span className="font-mono font-semibold">{expected}</span>.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="regen-confirm">Confirmation phrase</Label>
          <Input
            id="regen-confirm"
            type="text"
            autoComplete="off"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expected}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!armed || submitting}>
            {submitting ? 'Regenerating…' : 'Regenerate QR Code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
